/**
 * String utility functions
 */

/**
 * Capitalizes the first letter of a string
 *
 * @param str - The string to capitalize
 * @returns The string with the first letter capitalized, or the original string if empty
 *
 * @example
 * capitalizeFirst('hello') // Returns: 'Hello'
 * capitalizeFirst('world') // Returns: 'World'
 */
export const capitalizeFirst = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Converts a string to sentence case (first letter uppercase, rest lowercase).
 * Useful for placeholders and UI labels.
 *
 * @param str - The string to convert
 * @returns The string in sentence case, or the original string if empty
 *
 * @example
 * toSentenceCase('SELECT ACCOUNT LISTS') // Returns: 'Select account lists'
 * toSentenceCase('choose an option') // Returns: 'Choose an option'
 */
export const toSentenceCase = (str: string): string => {
  if (!str) return str;
  const lower = str.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export const toTitleCase = (str: string) => {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Lowercased mid-title, e.g. "VP of Sales".
const MINOR_WORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'by',
  'for',
  'in',
  'of',
  'on',
  'or',
  'the',
  'to',
]);

// Lowercase matches against common title acronyms so "vp of sales" renders "VP of Sales".
const TITLE_ACRONYMS = new Set([
  'vp',
  'svp',
  'evp',
  'avp',
  'ceo',
  'cfo',
  'cmo',
  'cto',
  'coo',
  'cio',
  'cro',
  'ciso',
  'hr',
  'it',
  'pr',
  'qa',
  'ux',
  'ui',
  'ai',
  'ml',
]);

export const toJobTitleCase = (str: string): string => {
  return str
    .split(/\s+/)
    .map((word, i) => {
      const lower = word.toLowerCase();
      if (i > 0 && MINOR_WORDS.has(lower)) return lower;
      return word
        .split('-')
        .map(part => {
          if (/^[A-Z]+$/.test(part)) return part;
          if (TITLE_ACRONYMS.has(part.toLowerCase())) return part.toUpperCase();
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        })
        .join('-');
    })
    .join(' ');
};

/**
 * Maximum allowed length for custom option values
 */
const MAX_CUSTOM_OPTION_LENGTH = 200;

/**
 * Patterns that indicate potentially malicious input
 * These will cause the input to be silently rejected
 */
const MALICIOUS_PATTERNS = [
  // XSS patterns
  /<script\b/i,
  /<\/script>/i,
  /javascript:/i,
  /on\w+\s*=/i, // onclick=, onerror=, etc.
  /<iframe/i,
  /<object/i,
  /<embed/i,
  /<form/i,
  /data:\s*text\/html/i,
  /vbscript:/i,

  // SQL injection patterns
  /;\s*(DROP|DELETE|TRUNCATE|ALTER|CREATE|INSERT|UPDATE)\s/i,
  /'\s*(OR|AND)\s+['"]?\d+['"]?\s*=\s*['"]?\d+/i, // ' OR '1'='1
  /'\s*(OR|AND)\s+['"]?\w+['"]?\s*=\s*['"]?\w+/i, // ' OR 'a'='a
  /UNION\s+(ALL\s+)?SELECT/i,
  /INTO\s+(OUTFILE|DUMPFILE)/i,
  /LOAD_FILE\s*\(/i,
  /BENCHMARK\s*\(/i,
  /SLEEP\s*\(/i,
  /WAITFOR\s+DELAY/i,
  /;\s*EXEC(\s|UTE)/i,
  /xp_cmdshell/i,
  /--\s*$/, // SQL comment at end
  /\/\*.*\*\//, // SQL block comment
];

/**
 * Sanitizes a custom option input string for safe use.
 * Returns the cleaned string if valid, or null if the input is empty, too long, or malicious.
 *
 * @param input - The raw input string to sanitize
 * @returns The sanitized string, or null if invalid/malicious
 *
 * @example
 * sanitizeCustomOption('  CEO  ') // Returns: 'CEO'
 * sanitizeCustomOption('<script>alert(1)</script>') // Returns: null (malicious)
 * sanitizeCustomOption('') // Returns: null (empty)
 * sanitizeCustomOption('A'.repeat(300)) // Returns: null (too long)
 */
export const sanitizeCustomOption = (input: string): string | null => {
  // Must be a non-empty string
  if (typeof input !== 'string') return null;

  // Trim whitespace
  let sanitized = input.trim();

  // Reject empty strings
  if (!sanitized) return null;

  // Reject if too long
  if (sanitized.length > MAX_CUSTOM_OPTION_LENGTH) return null;

  // Check for malicious patterns - silently reject
  for (const pattern of MALICIOUS_PATTERNS) {
    if (pattern.test(sanitized)) {
      return null;
    }
  }

  // Remove HTML tags (but keep the text content)
  sanitized = sanitized.replace(/<[^>]*>/g, '');

  // Decode common HTML entities
  sanitized = sanitized
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&nbsp;/g, ' ');

  // Re-check after cleaning - might now be empty
  sanitized = sanitized.trim();
  if (!sanitized) return null;

  return sanitized;
};
